/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useCallback, useEffect, useState } from 'react';
import axiosCall from '../../services/axios';
import useAbortController from './useAbortController';
import { isObjEmptyOrNull, getErrorFromEndpoint, isStrEmptyOrNull } from '../UtilityFunctions';
import { useAuthContext } from '../../contexts/AuthContext';

export function useGeography() {
  const [states, setStates] = useState([]);
  const [zipData, setZipData] = useState({});
  const { get } = useAbortController();
  const { token } = useAuthContext();
  const [errors, setErrors] = useState('');
  const [loading, setLoading] = useState(false);

  const getStates = useCallback(async () => {
    if (!token) return;
    const request = { url: process.env.REACT_APP_PUBLIC_STATES, method: 'get' };
    const config = get();
    const headers = {
      'Session-Token': token,
    };

    await axiosCall(request, config, headers)
      .then((res) => {
        if (!isObjEmptyOrNull(res)) {
          const err = getErrorFromEndpoint(res);
          if (isStrEmptyOrNull(err)) {
            setStates(res);
          } else {
            setErrors(err);
          }
        } else {
          setErrors('No states were found.');
          setStates([]);
        }
      })
      .catch((e: Error) => {
        console.log(e.message);
        setErrors(e.message);
      });
  }, [token, get]);

  const getZipData = useCallback(
    async (zipcode: string) => {
      const request = { url: process.env.REACT_APP_PUBLIC_ZIPCODE_DATA + zipcode, method: 'get' };
      const config = get();
      const headers = {
        'Session-Token': token,
      };

      await axiosCall(request, config, headers)
        .then((res) => {
          if (!isObjEmptyOrNull(res)) {
            const err = getErrorFromEndpoint(res);
            if (isStrEmptyOrNull(err)) {
              setZipData(res);
            } else {
              setErrors(err);
            }
          } else {
            setErrors('No zip data was found.');
            setZipData({});
          }
        })
        .catch((e: Error) => {
          console.log(e.message);
          setErrors(e.message);
        });
    },
    [token, get],
  );

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      await getStates();
    };
    fetchData()
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getStates]);

  return {
    states: states,
    zipData: zipData,
    getZipData: getZipData,
    errors: errors,
    loading: loading,
  };
}

export default useGeography;
